import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';

import routesPath from '@/shared/constants/routesPath';
import AuthProvider from '@/shared/contexts/AuthProvider';
import Protected from '@/shared/protectedRoutes';
import routesDefinition from '@/shared/routes';

import AuthLayout from '@/components/AuthLayout';
import ErrorBoundary from '@/components/ErrorBoundary';
import FourOFour from '@/components/FourOFour';
import Layout from '@/components/Layout';
import Loading from '@/components/ui/loading';
import useAuth from '@/hooks/useAuth';
import ForgotPassword from '@/pages/forgotPassword';
import Login from '@/pages/login';
import ResetPassword from '@/pages/resetPassword';

const AuthGate = ({ children }) => {
  const { isInitializing, isRefreshing } = useAuth();

  if (isInitializing || isRefreshing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loading />
      </div>
    );
  }

  return children;
};

const AuthProviderWrapper = ({ children }) => {
  return (
    <AuthProvider>
      <AuthGate>{children}</AuthGate>
    </AuthProvider>
  );
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      onError: (error, query) => {
        console.debug('Error', error);
        console.debug('Query', query);
        Sentry.captureException(error);
      },
      retry: 1,
    },
  },
});
const { protectedRoutes, publicRoutes } = routesDefinition;

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/">
      <Route
        element={<AuthLayout />}
        errorElement={import.meta.env.DEV ? null : <ErrorBoundary />}
      >
        <Route path={routesPath.LOGIN} element={<Login />} />
        <Route path={routesPath.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route path={routesPath.RESET_PASSWORD} element={<ResetPassword />} />
      </Route>

      <Route
        element={<Layout />}
        errorElement={import.meta.env.DEV ? null : <ErrorBoundary />}
      >
        {publicRoutes.map((route) => (
          <Route
            key={route.path}
            index={route.index}
            path={route.path}
            element={<route.view />}
          />
        ))}
        {protectedRoutes.map((route) => (
          <Route
            key={route.path}
            element={
              <Protected
                roles={route.roles}
                permissions={route.permissions}
                absoluteCheck={route.absoluteCheck}
              />
            }
          >
            <Route path={route.path} element={<route.view />} />
          </Route>
        ))}
        <Route path="*" element={<FourOFour />} />
      </Route>
    </Route>
  ),
  {
    basename: import.meta.env.BASE_URL || '/',
  }
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProviderWrapper>
        <RouterProvider router={router} />
      </AuthProviderWrapper>
    </QueryClientProvider>
  );
}

export default App;
